import { resolveConsistencyRule } from "./propFirmEngine";

/**
 * Règles DÉCLARÉES d'un compte prop firm, résolues phase d'abord puis palier.
 *
 * Partagé par la page de détails Advanced (PropFirmSection) et sa version Lite :
 * la résolution ne doit exister qu'une fois, sinon les deux écrans finiraient
 * par afficher des règles différentes pour le même compte.
 *
 * Les montants restent en montant OU pourcentage, comme le palier les déclare :
 * la moitié des paliers réels ne renseignent que l'un des deux.
 */
export interface EffectiveTierRules {
  profitTargetAmount: number | null;
  profitTargetPercent: number | null;
  maxDailyLossAmount: number | null;
  maxDailyLossPercent: number | null;
  maxTotalLossAmount: number | null;
  maxTotalLossPercent: number | null;
  trailingDrawdown: number | null;
  minTradingDays: number | null;
  payoutThreshold: number | null;
  payoutCooldownDays: number | null;
  consistencyRulePercent: number | null;
  maxContracts: number | null;
}

export function resolveEffectiveTierRules(account: any): EffectiveTierRules | null {
  const { activeTier, effectiveTemplate } = account;
  if (!effectiveTemplate || !activeTier) return null;
  const phase = account.resolvedPhase;
  const isPhaseTrailing = phase?.drawdownType === "trailing";
  const hasPhaseStaticDrawdown = !!phase?.drawdownType && !isPhaseTrailing;

  return {
    profitTargetPercent: phase?.profitTargetPercent ?? activeTier.profitTargetPercent,
    profitTargetAmount: phase?.profitTargetAmount ?? activeTier.profitTargetAmount,
    maxDailyLossAmount: phase?.maxDailyLossAmount ?? activeTier.maxDailyLossAmount,
    maxDailyLossPercent: phase?.maxDailyLossPercent ?? activeTier.maxDailyLossPercent,
    maxTotalLossAmount: hasPhaseStaticDrawdown ? (phase.maxDrawdownAmount ?? null) : activeTier.maxTotalLossAmount,
    maxTotalLossPercent: hasPhaseStaticDrawdown ? (phase.maxDrawdownPercent ?? null) : activeTier.maxTotalLossPercent,
    trailingDrawdown: isPhaseTrailing
      ? (phase.maxDrawdownAmount ?? (phase.maxDrawdownPercent != null ? account.initialBalance * phase.maxDrawdownPercent / 100 : null))
      : activeTier.hasTrailingDrawdown
        ? (activeTier.trailingDrawdownAmount ?? (activeTier.trailingDrawdownPercent != null ? account.initialBalance * activeTier.trailingDrawdownPercent / 100 : null))
        : null,
    minTradingDays: phase?.minTradingDays ?? activeTier.minTradingDays,
    payoutThreshold: phase?.payoutThresholdAmount ?? (activeTier.payoutThresholdPercent != null ? account.initialBalance * activeTier.payoutThresholdPercent / 100 : null),
    payoutCooldownDays: phase?.payoutCooldownDays ?? activeTier.payoutCooldownDays,
    // Même résolution que le moteur d'alertes (statut challenge / funded).
    consistencyRulePercent: resolveConsistencyRule(activeTier, phase ?? null, account.status, account.consistencyRulePercent),
    maxContracts: phase?.maxContracts ?? activeTier.maxContracts,
  };
}

/** Montant prioritaire, repli sur le pourcentage du solde initial. */
export function amountOrPercent(amount: number | null, percent: number | null, initialBalance: number): number | null {
  if (amount !== null) return amount;
  if (percent !== null) return initialBalance * percent / 100;
  return null;
}
